import { useTea, useTeaware } from '@/entities/product/api'
import { ProductCard } from '@/entities/product/ui'
import { TeaCard } from '@/entities/product/ui/tea-card'
import { Skeleton } from '@/shared/ui/skeleton'
import { cn } from '@/shared/lib/utils'

export interface ProductRecommendationsProps {
  /**
   * ID текущего товара (для исключения из рекомендаций)
   */
  currentProductId: string
  /**
   * Тип товара
   */
  productType: 'tea' | 'teaware'
  /**
   * ID категории товара (UUID для фильтрации рекомендаций)
   */
  categoryId?: string
  /**
   * Максимальное количество рекомендаций
   * @default 4
   */
  maxItems?: number
  /**
   * Заголовок секции
   * @default "Похожие товары"
   */
  title?: string
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * Виджет: Рекомендации похожих товаров
 * 
 * Компонент для отображения похожих товаров на основе категории.
 * Для чая использует TeaCard с выбором веса, для посуды — ProductCard.
 */
export function ProductRecommendations({
  currentProductId,
  productType,
  categoryId,
  maxItems = 4,
  title = 'Похожие товары',
  className,
}: ProductRecommendationsProps) {
  const teaQuery = useTea(
    productType === 'tea'
      ? {
          category: categoryId,
          limit: maxItems + 1,
          sort: 'popular',
        }
      : {}
  )

  const teawareQuery = useTeaware(
    productType === 'teaware'
      ? {
          category: categoryId,
          limit: maxItems + 1,
          sort: 'popular',
        }
      : {}
  )

  const query = productType === 'tea' ? teaQuery : teawareQuery
  const { data, isLoading, error } = query

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <h2 className="text-2xl font-bold text-ink dark:text-white">{title}</h2>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !data?.data) {
    return null
  }

  // Исключаем текущий товар и ограничиваем количество
  const recommendations = data.data
    .filter((product) => product.id !== currentProductId)
    .slice(0, maxItems)

  if (recommendations.length === 0) {
    return null
  }

  return (
    <div className={cn('px-4 py-6 space-y-4', className)}>
      <h2 className="text-xl font-semibold text-ink dark:text-white">{title}</h2>
      <div className="grid grid-cols-2 gap-4">
        {recommendations.map((product) => (
          productType === 'tea' ? (
            <TeaCard key={product.id} tea={product} />
          ) : (
            <ProductCard key={product.id} product={product} />
          )
        ))}
      </div>
    </div>
  )
}
